import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Order } from '../orders/models/order.models';
import { OrdersService } from '../orders/services/orders.service';
import { NotificationService } from './notification.service';
import { InvoiceData } from './pdf.service';
import { ProfileService } from './profile.service';
import { ReceiptPrinterService } from './receipt-printer.service';
import {
  BillingDetails,
  CardDetails,
  parseTransactionHistoryResponse,
  PaymentService,
  PaymentTransactionHistory,
} from './payment.service';
import { getUserTimezone } from '../shared/utils/date.util';

/** Everything needed to print a receipt when the caller already holds the order and payment. */
export interface OrderReceiptContext {
  order: Order | Record<string, any>;
  transaction: PaymentTransactionHistory;
  cardDetails?: CardDetails | null;
  billingDetails?: BillingDetails | null;
}

/** Optional hints when printing by order id (e.g. from a customer's service history). */
export interface PrintByOrderIdOptions {
  /** Picks the matching payment from the order's transaction history; defaults to the first. */
  paymentIntentId?: string | null;
}

const DEFAULT_BUSINESS_INFO: InvoiceData['businessInfo'] = {
  name: 'Way.com Car Wash',
  address: '',
  phone: '(555) 123-4567',
  email: 'support@way.com',
};

/**
 * Single entry point for every "Print Receipt" action in the app (checkout success modal,
 * order details payment tab, customer service history).
 *
 * Builds the {@link InvoiceData} the same way everywhere, hands it to
 * {@link ReceiptPrinterService.printReceipt} (network printer first, PDF fallback), and shows
 * the same success / fallback / error toasts. Callers should not build invoices or call
 * `ReceiptPrinterService.printReceipt` / `PdfService.printInvoice` directly for receipts.
 */
@Injectable({ providedIn: 'root' })
export class OrderReceiptService {
  constructor(
    private readonly ordersService: OrdersService,
    private readonly paymentService: PaymentService,
    private readonly profileService: ProfileService,
    private readonly receiptPrinterService: ReceiptPrinterService,
    private readonly messageService: NotificationService
  ) {}

  /** Prints a receipt when the caller already has the order and the transaction to print. */
  printReceipt(context: OrderReceiptContext): void {
    this.loadBusinessInfo().subscribe(businessInfo =>
      this.printAndNotify(this.buildInvoice(context, businessInfo))
    );
  }

  /**
   * Loads the order (mapped like Order Details) and its transaction history, then prints.
   * @param orderId The order's primary key (`orders.id`), not the `#…` display order number.
   */
  printReceiptForOrderId(orderId: string, options: PrintByOrderIdOptions = {}): void {
    const id = String(orderId ?? '').trim();
    if (!id) {
      this.notifyError('Order ID not available. Please try from the Order Details screen.');
      return;
    }

    // All three in parallel, so the business profile does not add a round trip after the rest.
    forkJoin({
      order: this.ordersService.getOrderById(id),
      txHistory: this.paymentService.getTransactionHistory(id),
      businessInfo: this.loadBusinessInfo(),
    })
      .pipe(take(1))
      .subscribe({
        next: ({ order, txHistory, businessInfo }) => {
          const parsed = parseTransactionHistoryResponse((txHistory as any)?.transactionHistory);
          const transaction = OrderReceiptService.pickTransaction(
            parsed.transactions,
            options.paymentIntentId
          );
          if (!transaction) {
            this.notifyError('No transaction record found for this order.');
            return;
          }

          this.printAndNotify(
            this.buildInvoice(
              {
                order,
                transaction,
                cardDetails: parsed.cardDetails,
                billingDetails: parsed.billingDetails,
              },
              businessInfo
            )
          );
        },
        error: () => this.notifyError('Could not load order details. Please try again.'),
      });
  }

  private loadBusinessInfo(): Observable<InvoiceData['businessInfo']> {
    return this.profileService.getBusinessProfile().pipe(
      take(1),
      map((response: any) => {
        const data = response?.status !== false ? response?.data : null;
        return data
          ? {
              name: data.name || DEFAULT_BUSINESS_INFO.name,
              address: '',
              phone: data.contact || DEFAULT_BUSINESS_INFO.phone,
              email: data.email || DEFAULT_BUSINESS_INFO.email,
            }
          : DEFAULT_BUSINESS_INFO;
      }),
      // A missing business profile should not block printing; fall back to defaults.
      catchError(() => of(DEFAULT_BUSINESS_INFO))
    );
  }

  private buildInvoice(
    { order, transaction, cardDetails, billingDetails }: OrderReceiptContext,
    businessInfo: InvoiceData['businessInfo']
  ): InvoiceData {
    const o = order as Record<string, any>;
    return {
      order: order as Order,
      transaction,
      customerInfo: {
        name:
          o['customerName'] ||
          o['customer_name'] ||
          `${o['customer_first_name'] || ''} ${o['customer_last_name'] || ''}`.trim() ||
          '',
        email: o['email'] || o['customer_email'] || '',
        phone: o['phone'] || o['customer_contact'] || '',
        address: o['branch'] || '',
      },
      businessInfo,
      cardDetails: cardDetails ?? null,
      billingDetails: billingDetails ?? null,
      receiptTimezone:
        o['locationTimezone'] || o['location_timezone'] || o['timezone'] || getUserTimezone(),
    };
  }

  private printAndNotify(invoice: InvoiceData): void {
    this.receiptPrinterService
      .printReceipt(invoice)
      .then(result => {
        if (result.method === 'network') {
          this.messageService.add({
            severity: 'success',
            summary: 'Receipt printed',
            detail: 'The receipt has been sent to the printer.',
          });
        } else if (result.printerError) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Printer unavailable',
            detail: `${result.printerError} A PDF receipt was opened instead.`,
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: 'Receipt ready',
            detail: 'A PDF receipt was opened for printing.',
          });
        }
      })
      .catch((err: unknown) =>
        this.notifyError(
          err instanceof Error && err.message
            ? err.message
            : 'There was an error generating the receipt. Please try again.'
        )
      );
  }

  private notifyError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Print failed', detail });
  }

  /**
   * Picks the payment to print. With a payment intent, only that payment is used, so a
   * multi-payment order never prints the wrong one; a lone payment is used as-is.
   */
  private static pickTransaction(
    transactions: PaymentTransactionHistory[],
    paymentIntentId?: string | null
  ): PaymentTransactionHistory | undefined {
    const intentId = paymentIntentId?.trim();
    if (!intentId || transactions.length === 1) {
      return transactions[0];
    }
    return transactions.find(t => t.payment_intent_id === intentId);
  }
}
